import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useAuth } from "@/context/providers/AuthProvider";
import { useCreditsContext } from "@/context/providers/CreditsProvider";
import { useToast } from "@/context/ToastContext";
import { CreateFormData } from "@/components/create/types";
import { CreatePageState, CreatePageAction } from "@/stores/createPageStore";
import type { CreditModalContext } from "@/components/create/creditModalContext";
import { CREATE_AUTH_HANDOFF_PARAM } from "@/utils/createAuthHandoffPersistence";
import { QUOTE_RESUME_ID_PARAM } from "@/utils/quoteResumePersistence";
import { CREDIT_RESUME_ID_PARAM } from "@/utils/creditAuthIntentPersistence";
import { projectSavedBackStaleness } from "@/lib/designer/previewFreshness";
import {
  CREDIT_CHECKOUT_RESTORE_ID_PARAM,
  isValidCreditCheckoutRestoreId,
} from "@/utils/creditCheckoutReturnUrl";
import { UrlParamValues } from "./utils/urlParamHelpers";
// Import merged hooks
import { useInitialization } from "./effects/useInitialization";
import { useGenerationLifecycle } from "./effects/useGenerationLifecycle";
import { useCreditManagement } from "./effects/useCreditManagement";
import { usePersistence } from "./effects/usePersistence";
import { useAutoTicketManagement } from "./effects/useAutoTicketManagement";

interface UseCreatePageEffectsProps {
  state: CreatePageState;
  dispatch: React.Dispatch<CreatePageAction>;
  agentSessionRestorationPending?: boolean;
  actions: {
    updateFormData: (data: Partial<CreateFormData>) => void;
    setStep: (step: number) => void;
    setError: (error: string | null) => void;
    showCreditModal: (context?: NonNullable<CreditModalContext>) => void;
    generateImages: () => Promise<void>;
    updateGeneratedTicket: (
      ticketId: string,
      frontImageUrl: string,
      frontImagePath: string | null,
      backImageUrl?: string | null,
      backImagePath?: string | null,
      modificationPrompt?: string,
      isModifyingFront?: boolean
    ) => Promise<void>;
  };
}

const CREDIT_RETURN_REFRESH_RETRY_DELAYS_MS = [
  0, 1000, 2000, 4000, 8000, 15000,
];

function getRefreshedCreditBalance(response: unknown): number | null {
  const balance = (response as { data?: { balance?: unknown } } | undefined)
    ?.data?.balance;
  return typeof balance === "number" ? balance : null;
}

export function useCreatePageEffects({
  state,
  dispatch,
  actions,
  agentSessionRestorationPending = false,
}: UseCreatePageEffectsProps) {
  const { trackCreateStep } = useAnalytics();
  const { session, roles, loading: authLoading } = useAuth();
  const {
    hasSufficientCredits,
    refresh: invalidateCredits,
    isLoading: creditsLoading,
    isFetching: creditsFetching,
    error: creditsError,
    balance: creditBalance,
  } = useCreditsContext();
  const { showToast } = useToast();
  const searchParams = useSearchParams();

  // Extract search param values to avoid enumeration warnings in Next.js 15
  // Use toString() to create a stable string representation for change detection
  // This avoids putting searchParams object directly in dependency arrays
  const searchParamsString = searchParams.toString();
  const isReturningFromCreditPurchase =
    searchParams.get("credits_purchased") === "true";
  const restoreFormRequested = searchParams.get("restore_form") === "true";
  const checkoutRestoreId = searchParams.get(CREDIT_CHECKOUT_RESTORE_ID_PARAM);
  const restoreIntentId =
    (isValidCreditCheckoutRestoreId(checkoutRestoreId)
      ? checkoutRestoreId
      : null) ??
    searchParams.get(CREATE_AUTH_HANDOFF_PARAM) ??
    searchParams.get(QUOTE_RESUME_ID_PARAM) ??
    searchParams.get(CREDIT_RESUME_ID_PARAM);
  const anonymousWorkAdoptionFailed =
    searchParams.get("_anon_work_adopted") === "0";
  const [isRefreshingCreditReturn, setIsRefreshingCreditReturn] =
    useState(false);
  const invalidateCreditsRef = useRef(invalidateCredits);

  useEffect(() => {
    invalidateCreditsRef.current = invalidateCredits;
  }, [invalidateCredits]);

  const searchParamValues: UrlParamValues = useMemo(() => {
    // Extract values using the searchParams object
    return {
      prompt: searchParams.get("prompt"),
      length: searchParams.get("length"),
      sleeve: searchParams.get("sleeve"),
      neckline: searchParams.get("neckline"),
      silhouette: searchParams.get("silhouette"),
      fabric: searchParams.get("fabric"),
      preset: searchParams.get("preset"),
      color: searchParams.get("color"),
    };
    // Use searchParamsString instead of searchParams to avoid enumeration warnings
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsString]);

  // Extract authoritative restore params separately for hooks that need them.
  // These are memoized separately to avoid enumeration warnings
  const modifyAndGalleryParams = useMemo(() => {
    return {
      modify: searchParams.get("modify"),
      gallery_design: searchParams.get("gallery_design"),
      gallery_action: searchParams.get("gallery_action"),
      ticket_id: searchParams.get("ticket_id"),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsString]);

  const modifyParam = modifyAndGalleryParams.modify;
  const galleryDesignParam = modifyAndGalleryParams.gallery_design;
  const galleryActionParam = modifyAndGalleryParams.gallery_action;
  const ticketIdParam = modifyAndGalleryParams.ticket_id;

  useEffect(() => {
    if (!isReturningFromCreditPurchase) {
      setIsRefreshingCreditReturn(false);
      return;
    }

    setIsRefreshingCreditReturn(true);

    let cancelled = false;
    let retryTimeout: number | null = null;

    const clearCreditReturnMarker = () => {
      if (typeof window === "undefined") {
        return;
      }

      const url = new URL(window.location.href);
      url.searchParams.delete("credits_purchased");
      window.history.replaceState({}, "", url.toString());
    };

    const scheduleCreditRefresh = (attempt: number) => {
      const runRefresh = async () => {
        try {
          const refreshedData = await invalidateCreditsRef.current(
            "credit_purchase_return"
          );
          if (cancelled) {
            return;
          }

          const refreshedBalance = getRefreshedCreditBalance(refreshedData);
          if (refreshedBalance !== null && refreshedBalance > 0) {
            clearCreditReturnMarker();
            setIsRefreshingCreditReturn(false);
            return;
          }
        } catch {
          if (cancelled) {
            return;
          }
        }

        const nextAttempt = attempt + 1;
        if (nextAttempt < CREDIT_RETURN_REFRESH_RETRY_DELAYS_MS.length) {
          scheduleCreditRefresh(nextAttempt);
        }
        // If credits are still not visible, keep the marker and suppression in
        // place for this page session. A reload will retry from the URL marker.
      };

      const delay = CREDIT_RETURN_REFRESH_RETRY_DELAYS_MS[attempt] ?? 0;
      if (delay <= 0) {
        void runRefresh();
        return;
      }

      retryTimeout = window.setTimeout(() => {
        retryTimeout = null;
        void runRefresh();
      }, delay);
    };

    scheduleCreditRefresh(0);

    return () => {
      cancelled = true;
      if (retryTimeout) {
        window.clearTimeout(retryTimeout);
      }
    };
  }, [isReturningFromCreditPurchase, searchParamsString]);

  // Memoize state for the legacy back-ticket recovery path.
  const autoTicketManagementState = useMemo(
    () => ({
      frontVariations:
        state.generation.images.variations.front.length > 0
          ? state.generation.images.variations.front
          : null,
      backVariations:
        state.generation.images.variations.back.length > 0
          ? state.generation.images.variations.back
          : null,
      selectedFrontVariationIndex: state.generation.images.selected.frontIndex,
      selectedBackVariationIndex: state.generation.images.selected.backIndex,
      backStale: projectSavedBackStaleness(state.generation.images).stale,
      persistedBackTicketId:
        state.generation.images.selected.backIndex !== null
          ? (state.ticket.variationTicketIds[
              state.generation.images.selected.backIndex
            ] ?? null)
          : null,
      ticketId: state.ticket.id,
      imageUrl: state.generation.images.front.url,
      imagePath: state.generation.images.front.path,
      loading: state.generation.progress.loading,
      isModifying: state.ui.modification.isModifying,
      formData: state.form.data,
    }),
    [
      state.generation.images,
      state.ticket.variationTicketIds,
      state.ticket.id,
      state.generation.progress.loading,
      state.ui.modification.isModifying,
      state.form.data,
    ]
  );

  // Priority 1: Initialization (merges: form restoration, URL params, ticket prefill, modification mode)
  const {
    restorationTimestampRef,
    restorationPending,
    retryTicketRestoration,
  } = useInitialization({
    state,
    dispatch,
    session,
    authLoading,
    showToast,
    updateFormData: actions.updateFormData,
    searchParamValues,
    modifyParam,
    galleryDesignParam,
    galleryActionParam,
    ticketIdParam,
    restoreFormRequested,
    restoreIntentId,
    anonymousWorkAdoptionFailed,
    agentSessionRestorationPending,
  });

  // Priority 2: Generation Lifecycle (merges: progress tracking, step tracking, analytics tracking)
  useGenerationLifecycle({
    state,
    dispatch,
    session,
    roles,
    trackCreateStep,
  });

  // Priority 3: Credit Management (auto-checking on step 1, invalidation after purchase)
  // Note: Credit balance is managed by useCreditsContext() hook as single source of truth
  useCreditManagement({
    state,
    session,
    roles,
    isReturningFromCreditPurchase:
      isReturningFromCreditPurchase || isRefreshingCreditReturn,
    creditsLoading,
    creditsError,
    creditsFetching,
    creditBalance,
    hasSufficientCredits,
    showCreditModal: actions.showCreditModal,
    setError: actions.setError,
  });

  // Priority 4: Persistence (merges: auto-save, variations persistence, before unload)
  usePersistence({
    state,
    dispatch,
    restorationTimestampRef,
  });

  // Priority 5: Recover only legacy back variations that lack a persisted
  // ticket association. Current generation responses are already committed.
  useAutoTicketManagement({
    state: autoTicketManagementState,
    dispatch,
    updateGeneratedTicket: actions.updateGeneratedTicket,
  });

  return { restorationPending, retryTicketRestoration };
}
